"use client";

import { useEffect, useState } from "react";
import { PencilIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/solid";
import GearItemFormDialog from "./GearItemFormDialog";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import Pagination from "../common/Pagination";
import { getErrorMessage } from "@/utils/withErrorHandling";

// Generic interface for gear item with id
interface GearItem {
  id: number;
  [key: string]: any;
}

// Interface for service methods
interface GearService<T extends GearItem, U> {
  getItems: (
    limit: number,
    sortField: string,
    sortOrder: string,
    page: number,
    search?: string
  ) => Promise<{
    data: T[];
    totalPages: number;
    totalItems: number;
  }>;
  createItem: (data: U) => Promise<T | null>;
  updateItem: (id: number, data: U) => Promise<T | null>;
  deleteItem: (id: number) => Promise<boolean>;
}

// Interface for table columns
interface TableColumn {
  field: string;
  header: string;
  sortable?: boolean;
  render?: (item: any) => React.ReactNode;
}

// Interface for form field definition
interface FormFieldDefinition {
  name: string;
  label: string;
  type: "text" | "select" | "brand-search";
  required?: boolean;
  options?: Array<{ value: string | number; label: string }>;
}

// Props for the generic component
interface GearItemsWithDialogProps<T extends GearItem, U> {
  title: string;
  service: GearService<T, U>;
  columns: TableColumn[];
  formFields: FormFieldDefinition[];
  itemTypeNameSingular: string;
  itemTypeNamePlural: string;
  requestDtoFactory: (formData: Record<string, any>) => U;
  canDeleteItem?: (item: T) => { can: boolean; reason?: string };
  initialSearch?: string;
  onSearchChange?: (search: string) => void;
  initialPage?: number;
  onPageChange?: (page: number) => void;
  initialLimit?: number;
  onLimitChange?: (limit: number) => void;
}

function GearItemsWithDialog<T extends GearItem, U>({
  title,
  service,
  columns,
  formFields,
  itemTypeNameSingular,
  itemTypeNamePlural,
  requestDtoFactory,
  canDeleteItem = () => ({ can: true }),
  initialSearch = "",
  onSearchChange,
  initialPage = 1,
  onPageChange,
  initialLimit = 5,
  onLimitChange,
}: GearItemsWithDialogProps<T, U>) {
  // State for data
  const [items, setItems] = useState<T[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // State for UI
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [apiError, setApiError] = useState("");

  // State for search
  const [searchValue, setSearchValue] = useState(initialSearch);
  const [debouncedSearchValue, setDebouncedSearchValue] =
    useState(initialSearch);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);

  // Handle limit change
  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing items per page

    // Notify parent component if callback provided
    if (onLimitChange) {
      onLimitChange(newLimit);
    }
  };

  // State for sorting
  const [sortField, setSortField] = useState(columns[0]?.field || "id");
  const [sortOrder, setSortOrder] = useState("asc");

  // State for modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [itemToDelete, setItemToDelete] = useState<T | null>(null);
  const [modalTitle, setModalTitle] = useState(
    `Add New ${itemTypeNameSingular}`
  );

  // Debounce search value
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      setDebouncedSearchValue(searchValue);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(debounceTimer);
    };
  }, [searchValue]);

  // Update search value when initialSearch changes
  useEffect(() => {
    if (initialSearch !== searchValue) {
      setSearchValue(initialSearch);
      setDebouncedSearchValue(initialSearch);
    }
  }, [initialSearch, searchValue]);

  // Update page when initialPage changes
  useEffect(() => {
    if (initialPage !== currentPage) {
      setCurrentPage(initialPage);
    }
  }, [initialPage, currentPage]);

  // Update limit when initialLimit changes
  useEffect(() => {
    if (initialLimit !== limit) {
      setLimit(initialLimit);
    }
  }, [initialLimit, limit]);

  // Fetch items on component mount and when dependencies change
  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, limit, sortField, sortOrder, debouncedSearchValue]);

  // Handle search input - simpler without directly triggering API calls
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setCurrentPage(1); // Reset to first page when searching

    // Notify parent component of search change if callback is provided
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (field === sortField) {
      // Toggle order if clicking the same field
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Fetch items from API
  const fetchItems = async () => {
    setIsLoading(true);
    setHasError(false);

    try {
      const response = await service.getItems(
        limit,
        sortField,
        sortOrder,
        currentPage,
        debouncedSearchValue || undefined
      );

      setItems(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalItems);
    } catch (error) {
      console.error(`Error fetching ${itemTypeNamePlural}:`, error);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle adding a new item
  const handleAddItem = () => {
    setEditingItem(null);
    setModalTitle(`Add New ${itemTypeNameSingular}`);
    setApiError("");
    setIsModalOpen(true);
  };

  // Handle editing an item
  const handleEditItem = (item: T) => {
    setEditingItem(item);
    setModalTitle(`Edit ${itemTypeNameSingular}`);
    setApiError("");
    setIsModalOpen(true);
  };

  // Handle deleting an item
  const handleDeleteItem = (item: T) => {
    setItemToDelete(item);
    setIsDeleteModalOpen(true);
  };

  // Save item (create or update)
  const saveItem = async (formData: Record<string, any>) => {
    setApiError("");

    try {
      // Prepare the item data
      const itemData = requestDtoFactory(formData);

      let result;

      if (editingItem) {
        // Update existing item
        result = await service.updateItem(editingItem.id, itemData);
      } else {
        // Create new item
        result = await service.createItem(itemData);
      }

      if (result) {
        // Success - refresh the items list and close the modal
        fetchItems();
        setIsModalOpen(false);
      } else {
        // If the result is null, there was an error but no specific message
        setApiError(
          `Failed to ${
            editingItem ? "update" : "create"
          } ${itemTypeNameSingular}. Please try again.`
        );
      }
    } catch (error: unknown) {
      // Get an appropriate error message
      const context = editingItem
        ? `updating ${itemTypeNameSingular}`
        : `creating ${itemTypeNameSingular}`;
      const errorMessage = getErrorMessage(error, context);
      setApiError(errorMessage);
    }
  };

  // Confirm deleting an item
  const confirmDeleteItem = async () => {
    if (!itemToDelete) return;

    setApiError("");
    setHasError(false);

    try {
      const success = await service.deleteItem(itemToDelete.id);

      if (success) {
        // Success - refresh the items list and close the modal
        fetchItems();
        setIsDeleteModalOpen(false);
      } else {
        // If the operation was not successful but didn't throw an error
        setApiError(
          `Failed to delete ${itemTypeNameSingular}. Please try again.`
        );
      }
    } catch (error: unknown) {
      // Get an appropriate error message
      const errorMessage = getErrorMessage(
        error,
        `deleting ${itemTypeNameSingular}`
      );
      setApiError(errorMessage);
      console.error(`Error deleting ${itemTypeNameSingular}:`, error);
      // We don't close the modal so the user can see the error
    }
  };

  // Map form fields to format expected by GearItemFormDialog
  const mappedFormFields = formFields.map((field) => ({
    ...field,
    type: field.type as "text" | "select" | "brand-search",
    initialValue: editingItem ? editingItem[field.name] : undefined,
  }));

  // Render function
  return (
    <div>
      {/* Header with add button */}
      <div className="mb-4 flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
        <button
          onClick={handleAddItem}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add {itemTypeNameSingular}
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            className="block w-full rounded-md border border-gray-300 py-2 px-3 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            placeholder={`Search ${itemTypeNamePlural}...`}
          />
          <div className="absolute inset-y-0 right-0 flex items-center pr-3">
            {searchValue && (
              <button
                onClick={() => {
                  setSearchValue("");
                  setCurrentPage(1); // Reset to first page when clearing search

                  // Notify parent component of search change if callback is provided
                  if (onSearchChange) {
                    onSearchChange("");
                  }
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Error message */}
      {hasError && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon
                className="h-5 w-5 text-red-400"
                aria-hidden="true"
              />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading {itemTypeNamePlural}
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>
                  There was an error loading the {itemTypeNamePlural}. Please
                  try refreshing the page.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Items table */}
      {isLoading ? (
        <div className="text-center py-8">
          <p className="text-gray-600">Loading...</p>
        </div>
      ) : items && items.length > 0 ? (
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.field}
                    scope="col"
                    className={`py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 ${
                      column.sortable !== false ? "cursor-pointer" : ""
                    }`}
                    onClick={() =>
                      column.sortable !== false && handleSort(column.field)
                    }
                  >
                    <div className="flex items-center">
                      {column.header}
                      {column.sortable !== false &&
                        sortField === column.field && (
                          <span className="ml-2">
                            {sortOrder === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                    </div>
                  </th>
                ))}
                <th
                  scope="col"
                  className="relative py-3.5 pl-3 pr-4 sm:pr-6 text-right"
                >
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((item) => (
                <tr key={item.id}>
                  {columns.map((column) => (
                    <td
                      key={`${item.id}-${column.field}`}
                      className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6"
                    >
                      {column.render
                        ? column.render(item)
                        : item[column.field] || "N/A"}
                    </td>
                  ))}
                  <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                    <button
                      onClick={() => handleEditItem(item)}
                      className="mr-2 text-indigo-600 hover:text-indigo-900"
                    >
                      <PencilIcon className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only">Edit</span>
                    </button>

                    {/* Delete button with conditional disabling */}
                    {(() => {
                      const deleteCheck = canDeleteItem(item);
                      return (
                        <button
                          onClick={() => handleDeleteItem(item)}
                          className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={!deleteCheck.can}
                          title={
                            deleteCheck.can
                              ? `Delete ${itemTypeNameSingular}`
                              : deleteCheck.reason ||
                                `Cannot delete this ${itemTypeNameSingular}`
                          }
                        >
                          <TrashIcon className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">Delete</span>
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600">
            No {itemTypeNamePlural} found.{" "}
            {searchValue && "Try a different search term."}
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            onPageChange={(page) => {
              setCurrentPage(page);
              // Notify parent component of page change if callback is provided
              if (onPageChange) {
                onPageChange(page);
              }
            }}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </div>
      )}

      {/* Add/Edit Item Modal */}
      <GearItemFormDialog
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={modalTitle}
        fields={mappedFormFields}
        onSave={saveItem}
        editingItem={editingItem}
        apiError={apiError}
      />

      {/* Delete Confirmation Modal */}
      {itemToDelete && (
        <ConfirmDeleteDialog
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setApiError(""); // Clear any error when closing
          }}
          onConfirm={confirmDeleteItem}
          title={`Delete ${itemTypeNameSingular}`}
          itemName={
            itemToDelete[
              Object.keys(itemToDelete).find(
                (key) =>
                  typeof itemToDelete[key] === "string" &&
                  key !== "id" &&
                  key.toLowerCase().includes("name")
              ) || Object.keys(itemToDelete)[1]
            ] || `this ${itemTypeNameSingular}`
          }
          itemType={itemTypeNameSingular.toLowerCase()}
          warningMessage={
            !canDeleteItem(itemToDelete).can
              ? canDeleteItem(itemToDelete).reason
              : undefined
          }
          errorMessage={apiError || undefined}
        />
      )}
    </div>
  );
}

export default GearItemsWithDialog;
